import { useConfirmation } from "shared/components/ConfirmationProvider";
import ThemedElement from "shared/components/ThemedElement";
import React, { useState, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import {
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Box,
  InputAdornment,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  Search,
  Delete,
  Edit,
  Add,
  Close,
  Person as UserIcon,
  Visibility,
  FilterList,
  CheckCircle,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Business,
  Language,
  Description,
  Block,
  PowerSettingsNew,
} from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import MDInput from "shared/components/MDInput";
import DataTable from "shared/components/Tables/DataTable";
import PropiedadDialog from "../ModalPropiedades/PropiedadDialog";
import SuccessDialog from "../ModalPropiedades/SuccessDialog";

const TablePropiedades = forwardRef(
  (
    {
      propiedades = [],
      loading = false,
      error = null,
      onRefresh = null,
      onAddPropiedad = null,
      onEditPropiedad = null,
      onDeletePropiedad = null,
      onToggleStatus = null,
      currentUser = null,
      // 🔥 PERMISOS
      hasCreatePermission = false,
      hasEditPermission = false,
      hasActivatePermission = false,
      hasDeactivatePermission = false,
    },
    ref
  ) => {
    const confirm = useConfirmation();

    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState("");
    const [selectedPropiedad, setSelectedPropiedad] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [statusMessage, setStatusMessage] = useState("");
    const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
    const [createdPropiedad, setCreatedPropiedad] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const columns = [
      { Header: "ID", accessor: "id", width: "5%" },
      { Header: "Nombre", accessor: "name", width: "18%" },
      { Header: "IP", accessor: "ip", width: "18%" },
      { Header: "Descripción", accessor: "description", width: "18%" },
      { Header: "ID Copropietario", accessor: "idcopropietarie", width: "13%" },
      { Header: "Creado por", accessor: "user_create", width: "10%" },
      { Header: "Estado", accessor: "status", width: "8%" },
      { Header: "Acciones", accessor: "actions", width: "10%" },
    ];

    const renderStatus = (propiedad) => {
      const isActive = propiedad.status === 1;
      return (
        <Chip
          label={isActive ? "ACTIVO" : "INACTIVO"}
          size="small"
          color={isActive ? "success" : "error"}
          icon={isActive ? <CheckCircle /> : <ErrorIcon />}
          sx={{
            fontWeight: "bold",
            fontSize: "0.65rem",
            height: "26px",
            "& .MuiChip-icon": {
              fontSize: "0.9rem",
            },
          }}
        />
      );
    };

    // 🔥 CORREGIDO: Verifica permisos antes de mostrar botones
    const renderActions = (propiedad) => {
      if (!propiedad) {
        return (
          <Box display="flex" gap={0.5}>
            <Tooltip title="Sin datos">
              <IconButton size="small" disabled>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }

      const isActive = propiedad.status === 1;

      // Verificar si tiene algún permiso para mostrar la columna de acciones
      const puedeEditar = hasEditPermission;
      const puedeActivar = !isActive && hasActivatePermission;
      const puedeDesactivar = isActive && hasDeactivatePermission;
      const tieneAlgunaAccion = puedeEditar || puedeActivar || puedeDesactivar;

      if (!tieneAlgunaAccion) {
        return (
          <MDTypography variant="caption" color="text.secondary">
            Sin acciones
          </MDTypography>
        );
      }

      return (
        <Box display="flex" gap={0.5}>
          {/* 🔥 Botón Activar/Desactivar - Solo si tiene permiso */}
          {(puedeActivar || puedeDesactivar) && (
            <Tooltip title={isActive ? "Desactivar" : "Activar"}>
              <IconButton
                size="small"
                color={isActive ? "warning" : "success"}
                onClick={() => handleToggleStatusClick(propiedad)}
                sx={{
                  "&:hover": {
                    backgroundColor: isActive
                      ? "rgba(237, 108, 2, 0.08)"
                      : "rgba(76, 175, 80, 0.08)",
                  },
                }}
              >
                {isActive ? (
                  <PowerSettingsNew fontSize="small" />
                ) : (
                  <CheckCircle fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}

          {/* 🔥 Botón Editar - Solo si tiene permiso */}
          {puedeEditar && (
            <Tooltip title="Editar">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEditClick(propiedad)}
                sx={{
                  "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.08)" },
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    };

    const renderUserCreate = (userCreate) => {
      if (!userCreate)
        return (
          <MDTypography variant="body2" color="text.secondary">
            ---
          </MDTypography>
        );
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <UserIcon fontSize="small" color="info" />
          <MDTypography variant="body2" sx={{ fontSize: "0.8rem" }}>
            {userCreate}
          </MDTypography>
        </Box>
      );
    };

    const handleFilterChange = (event, newFilterType) => {
      if (newFilterType !== null) {
        setFilterType(newFilterType);
      }
    };

    const getFilteredPropiedades = () => {
      let filtered = [...propiedades];

      if (filterType === "active") {
        filtered = filtered.filter((propiedad) => propiedad.status === 1);
      } else if (filterType === "inactive") {
        filtered = filtered.filter((propiedad) => propiedad.status === 0);
      }

      if (!searchQuery.trim()) return filtered;
      const query = searchQuery.toLowerCase().trim();
      return filtered.filter((propiedad) => {
        const searchableFields = [
          propiedad.name?.toLowerCase() || "",
          propiedad.ip?.toLowerCase() || "",
          propiedad.description?.toLowerCase() || "",
          propiedad.idcopropietarie?.toString().toLowerCase() || "",
          propiedad.user_create?.toLowerCase() || "",
        ];
        return searchableFields.some((field) => field.includes(query));
      });
    };

    const filteredData = getFilteredPropiedades();
    const activeCount = propiedades.filter((p) => p.status === 1).length;
    const inactiveCount = propiedades.filter((p) => p.status === 0).length;

    const rows = filteredData.map((propiedad) => ({
      id: (
        <MDTypography variant="body2" color="text.secondary">
          {propiedad.id}
        </MDTypography>
      ),
      name: (
        <Box display="flex" alignItems="center" gap={1}>
          <Business fontSize="small" color="primary" />
          <MDTypography variant="body1" fontWeight="bold" sx={{ fontSize: "0.875rem" }}>
            {propiedad.name || "Sin nombre"}
          </MDTypography>
        </Box>
      ),
      ip: (
        <Box display="flex" alignItems="center" gap={1}>
          <Language fontSize="small" color="info" />
          <MDTypography variant="body2">{propiedad.ip || "N/A"}</MDTypography>
        </Box>
      ),
      description: (
        <Box display="flex" alignItems="center" gap={1}>
          <Description fontSize="small" color="action" />
          <MDTypography variant="body2" sx={{ fontSize: "0.8rem" }}>
            {propiedad.description || "---"}
          </MDTypography>
        </Box>
      ),
      idcopropietarie: (
        <MDTypography variant="body2" fontWeight="medium">
          {propiedad.idcopropietarie || "N/A"}
        </MDTypography>
      ),
      user_create: renderUserCreate(propiedad.user_create),
      status: renderStatus(propiedad),
      actions: renderActions(propiedad),
    }));

    const handleAddClick = () => {
      setDialogType("add");
      setSelectedPropiedad(null);
      setSubmitStatus(null);
      setStatusMessage("");
      setIsSubmitting(false);
      setOpenDialog(true);
    };

    const handleEditClick = (propiedad) => {
      setDialogType("edit");
      setSelectedPropiedad(propiedad);
      setSubmitStatus(null);
      setStatusMessage("");
      setIsSubmitting(false);
      setOpenDialog(true);
    };

    const handleDeleteClick = (propiedad) => {
      setDialogType("delete");
      setSelectedPropiedad(propiedad);
      setOpenDialog(true);
    };

    const handleToggleStatusClick = (propiedad) => {
      setSelectedPropiedad(propiedad);
      setPendingAction("toggle");
      setOpenConfirmDialog(true);
    };

    const handleConfirmToggle = async () => {
      setOpenConfirmDialog(false);
      setIsSubmitting(true);
      setStatusMessage("Cambiando estado de la propiedad...");
      setSubmitStatus(null);

      try {
        const result = await onToggleStatus(selectedPropiedad.id, selectedPropiedad.status);
        if (result && result.success) {
          setSubmitStatus("success");
          setStatusMessage(result.message);
          if (onRefresh) {
            setTimeout(async () => {
              await onRefresh();
            }, 1500);
          }
          setTimeout(() => {
            setSubmitStatus(null);
            setStatusMessage("");
          }, 2000);
        } else {
          setSubmitStatus("error");
          setStatusMessage(result?.error || "Error al cambiar el estado");
        }
      } catch (error) {
        setSubmitStatus("error");
        setStatusMessage("Error al cambiar el estado");
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleDialogClose = async () => {
      if (isSubmitting) {
        if (await confirm("La operación está en progreso. ¿Desea cancelar?")) {
          setIsSubmitting(false);
          setOpenDialog(false);
        }
      } else {
        setOpenDialog(false);
        setSelectedPropiedad(null);
        setSubmitStatus(null);
        setStatusMessage("");
        setIsSubmitting(false);
      }
    };

    const handleDialogConfirm = async (formData) => {
      if (dialogType === "delete") {
        setIsSubmitting(true);
        setStatusMessage("Eliminando propiedad...");
        setSubmitStatus(null);

        try {
          const result = await onDeletePropiedad(selectedPropiedad.id);
          if (result && result.success) {
            setSubmitStatus("success");
            setStatusMessage("Propiedad eliminada exitosamente");
            if (onRefresh) {
              setTimeout(async () => {
                await onRefresh();
              }, 1500);
            }
            setTimeout(() => {
              handleDialogClose();
            }, 1500);
          } else {
            setSubmitStatus("error");
            setStatusMessage(result?.error || "Error al eliminar la propiedad");
          }
        } catch (error) {
          setSubmitStatus("error");
          setStatusMessage("Error al eliminar la propiedad");
        } finally {
          setIsSubmitting(false);
        }
        return;
      }

      setIsSubmitting(true);
      setSubmitStatus(null);
      setStatusMessage(
        dialogType === "add" ? "Agregando propiedad..." : "Actualizando propiedad..."
      );

      try {
        let result;
        if (dialogType === "add") {
          const newPropiedad = {
            ...formData,
            user_create: formData.user_create || currentUser?.username || "",
          };
          result = await onAddPropiedad(newPropiedad);

          if (result && result.success) {
            setCreatedPropiedad(result.data || newPropiedad);
          }
        } else if (dialogType === "edit") {
          const updatedPropiedad = {
            id: selectedPropiedad.id,
            name: formData.name,
            ip: formData.ip,
            description: formData.description || "",
            config: selectedPropiedad.config || "",
          };

          result = await onEditPropiedad(updatedPropiedad);
        }

        if (result && result.success) {
          setSubmitStatus("success");
          setStatusMessage(
            dialogType === "add"
              ? "Propiedad agregada exitosamente"
              : "Propiedad actualizada exitosamente"
          );

          if (dialogType === "add") {
            setTimeout(() => {
              handleDialogClose();
              setOpenSuccessDialog(true);
              if (onRefresh) {
                setTimeout(async () => {
                  await onRefresh();
                }, 500);
              }
            }, 500);
          } else {
            setTimeout(() => {
              handleDialogClose();
              if (onRefresh) {
                setTimeout(async () => {
                  await onRefresh();
                }, 500);
              }
            }, 1500);
          }
        } else {
          setSubmitStatus("error");
          setStatusMessage(result?.error || "Error al realizar la operación");
        }
      } catch (error) {
        console.error("❌ Error en handleDialogConfirm:", error);
        setSubmitStatus("error");
        setStatusMessage(
          dialogType === "add"
            ? "Error al agregar la propiedad"
            : "Error al actualizar la propiedad"
        );
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleSearchChange = (e) => {
      setSearchQuery(e.target.value);
    };

    const clearSearch = () => {
      setSearchQuery("");
    };

    const resetFilters = () => {
      setSearchQuery("");
      setFilterType("all");
    };

    // 🔥 CORREGIDO: openAddModal verifica permiso
    useImperativeHandle(ref, () => ({
      openAddModal: () => {
        if (hasCreatePermission) {
          handleAddClick();
        }
      },
    }));

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Box textAlign="center">
            <CircularProgress color="primary" size={40} />
            <MDTypography variant="h6" mt={2}>
              Cargando propiedades...
            </MDTypography>
          </Box>
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={3}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
            <MDTypography variant="h6" fontWeight="bold">
              Error al cargar propiedades
            </MDTypography>
            <MDTypography variant="body1">{error}</MDTypography>
          </Alert>
          <Box display="flex" justifyContent="center" mt={2}>
            <MDButton variant="contained" color="primary" onClick={onRefresh}>
              Reintentar carga
            </MDButton>
          </Box>
        </Box>
      );
    }

    const hasFilters = searchQuery || filterType !== "all";
    const showEmptyState = filteredData.length === 0;

    return (
      <Box>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            backgroundColor: "#f8f9fa",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box sx={{ flex: 1, minWidth: "250px" }}>
              <MDInput
                fullWidth
                placeholder="Buscar por nombre, IP, descripción o copropietario..."
                value={searchQuery}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#9e9e9e", fontSize: "1.2rem" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <Close fontSize="small" sx={{ color: "#9e9e9e" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0d47a1",
                    },
                  },
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "42px",
                    fontSize: "0.9rem",
                  },
                }}
              />
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
              <FilterList sx={{ color: "#757575", fontSize: "1.1rem" }} />
              <ToggleButtonGroup
                value={filterType}
                exclusive
                onChange={handleFilterChange}
                aria-label="Filtro de propiedades"
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    px: 2.5,
                    py: 0.6,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "white",
                    color: "#757575",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(13, 71, 161, 0.04)",
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#0d47a1",
                      color: "white !important",
                      borderColor: "#0d47a1",
                      boxShadow: "0 2px 8px rgba(13, 71, 161, 0.2)",
                      "&:hover": {
                        backgroundColor: "#1565c0",
                        color: "white !important",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "white !important",
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="all" aria-label="Todos">
                  <Visibility fontSize="small" sx={{ mr: 0.8, fontSize: "1rem" }} />
                  Todos ({propiedades.length})
                </ToggleButton>
                <ToggleButton value="active" aria-label="Activos">
                  <CheckCircle fontSize="small" sx={{ mr: 0.8, fontSize: "1rem" }} />
                  Activos ({activeCount})
                </ToggleButton>
                <ToggleButton value="inactive" aria-label="Inactivos">
                  <ErrorIcon fontSize="small" sx={{ mr: 0.8, fontSize: "1rem" }} />
                  Inactivos ({inactiveCount})
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #e0e0e0",
          }}
        >
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={10}
            showTotalEntries={true}
            noEndBorder={true}
            canSearch={false}
            pagination={{ variant: "gradient", color: "primary" }}
            sx={{
              "& .MuiTableRow-root": {
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(13, 71, 161, 0.02)",
                },
              },
              "& .MuiTableCell-head": {
                backgroundColor: "#f5f7fa",
                fontWeight: 600,
                color: "#424242",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </Paper>

        {showEmptyState && (
          <Box p={4} textAlign="center">
            <Search sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
            <MDTypography variant="h5" fontWeight="bold" gutterBottom>
              {hasFilters ? "No se encontraron resultados" : "No hay propiedades registradas"}
            </MDTypography>
            <MDTypography variant="body1" color="text.secondary">
              {hasFilters
                ? `No hay propiedades que coincidan con los filtros aplicados.`
                : "No se encontraron propiedades en el sistema."}
            </MDTypography>
            <Box display="flex" justifyContent="center" gap={2} mt={2} flexWrap="wrap">
              {hasFilters && (
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={resetFilters}
                  startIcon={<RefreshIcon />}
                  sx={{ borderRadius: "8px" }}
                >
                  Mostrar Todos
                </MDButton>
              )}
              {!hasFilters && hasCreatePermission && (
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={handleAddClick}
                  startIcon={<Add />}
                  sx={{ borderRadius: "8px" }}
                >
                  Agregar Nueva Propiedad
                </MDButton>
              )}
            </Box>
          </Box>
        )}

        <PropiedadDialog
          open={openDialog}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
          dialogType={dialogType}
          selectedPropiedad={selectedPropiedad}
          currentUser={currentUser}
          isSubmitting={isSubmitting}
          submitStatus={submitStatus}
          statusMessage={statusMessage}
        />

        <SuccessDialog
          open={openSuccessDialog}
          onClose={() => {
            setOpenSuccessDialog(false);
            setCreatedPropiedad(null);
          }}
          data={createdPropiedad}
          title="¡Propiedad Creada Exitosamente!"
          subtitle="La propiedad ha sido registrada en el sistema"
          entityType="propiedad"
          autoClose={true}
          autoCloseDelay={3000}
        />

        <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
          <DialogTitle>
            {selectedPropiedad?.status === 1 ? "Desactivar Propiedad" : "Activar Propiedad"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Está seguro de que desea {selectedPropiedad?.status === 1 ? "desactivar" : "activar"}{" "}
              la propiedad {selectedPropiedad?.name}?
              <br />
              <ThemedElement
                component="span"
                style={{ color: selectedPropiedad?.status === 1 ? "#ed6c02" : "#2e7d32" }}
              >
                {selectedPropiedad?.status === 1
                  ? "La propiedad dejará de estar disponible."
                  : "La propiedad volverá a estar disponible."}
              </ThemedElement>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => setOpenConfirmDialog(false)}
              sx={{ borderRadius: "8px" }}
            >
              Cancelar
            </MDButton>
            <MDButton
              variant="contained"
              color={selectedPropiedad?.status === 1 ? "warning" : "success"}
              onClick={handleConfirmToggle}
              disabled={isSubmitting}
              startIcon={selectedPropiedad?.status === 1 ? <PowerSettingsNew /> : <CheckCircle />}
              sx={{ borderRadius: "8px" }}
            >
              {isSubmitting
                ? "Procesando..."
                : selectedPropiedad?.status === 1
                ? "Desactivar"
                : "Activar"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);

TablePropiedades.propTypes = {
  propiedades: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRefresh: PropTypes.func,
  onAddPropiedad: PropTypes.func,
  onEditPropiedad: PropTypes.func,
  onDeletePropiedad: PropTypes.func,
  onToggleStatus: PropTypes.func,
  currentUser: PropTypes.object,
  hasCreatePermission: PropTypes.bool,
  hasEditPermission: PropTypes.bool,
  hasActivatePermission: PropTypes.bool,
  hasDeactivatePermission: PropTypes.bool,
};

TablePropiedades.defaultProps = {
  propiedades: [],
  loading: false,
  error: null,
  onRefresh: null,
  onAddPropiedad: null,
  onEditPropiedad: null,
  onDeletePropiedad: null,
  onToggleStatus: null,
  currentUser: null,
  hasCreatePermission: false,
  hasEditPermission: false,
  hasActivatePermission: false,
  hasDeactivatePermission: false,
};

export default TablePropiedades;
